import { createElement as h, useContext, useState } from 'react';
import { GlobalDataContext } from '@ea-lab/reactive-json';

/**
 * usePagination — remplace le hook de pagination du cœur reactive-json
 * (`plugins.hook.usePagination`, lu par `Switch`).
 *
 * POURQUOI LE REMPLACER. Celui du cœur garde l'index de page dans un `useState`
 * qu'il ne rapproche JAMAIS du contenu reçu. Une liste paginée ET filtrable
 * (une recherche, une facette) casse donc en silence : on est page 3, on tape
 * un mot qui ne laisse qu'une page de résultats, `slice(40, 60)` rend un
 * tableau VIDE — et les boutons de page ont disparu en même temps (le cœur ne
 * les rend pas en dessous de deux pages), donc plus rien ne permet de revenir.
 * Aucun YAML ne rattrape ça : la seule prise est ce point d'extension.
 *
 * LE CORRECTIF EST UNE DÉRIVATION, PAS UN EFFET. L'index affiché est BORNÉ à la
 * dernière page existante au moment du rendu (`Math.min`). Rien n'est
 * réinitialisé, aucun `useEffect` ne court après le rendu : une liste qui
 * rétrécit montre sa dernière page au lieu du vide, et si elle regrossit
 * (recherche effacée) l'utilisateur retrouve la page où il était. C'est le
 * comportement le moins surprenant, et le seul qui ne perde jamais l'écran.
 *
 * Le reste — la fenêtre de boutons, les composants de rendu pris dans
 * `plugins.pagination`, la forme du retour — est identique au cœur : ce hook
 * doit rester interchangeable avec lui.
 */
export const usePagination = ({
  customComponents = {},
  containerProps = {},
  dataToPaginate = [],
  forcePaginationDisplay = false,
  itemProps = {},
  maxPageButtonsCount = 5,
  pageMaxItemCount = 10,
}) => {
  const globalDataContext = useContext(GlobalDataContext);
  const registered = globalDataContext?.plugins?.pagination ?? {};

  const Container = customComponents.Container ?? registered.Container ?? DefaultContainer;
  const First = customComponents.First ?? registered.First ?? DefaultButton;
  const Prev = customComponents.Prev ?? registered.Prev ?? DefaultButton;
  const Item = customComponents.Item ?? registered.Item ?? DefaultItem;
  const Ellipsis = customComponents.Ellipsis ?? registered.Ellipsis ?? DefaultEllipsis;
  const Next = customComponents.Next ?? registered.Next ?? DefaultButton;
  const Last = customComponents.Last ?? registered.Last ?? DefaultButton;

  const [requestedPage, setRequestedPage] = useState(0);

  const getPageCountForContent = (content) => Math.ceil((content?.length ?? 0) / pageMaxItemCount);
  const pageCount = getPageCountForContent(dataToPaginate);

  // LA correction : l'index demandé est borné au contenu REÇU à ce rendu.
  // `pageCount - 1` peut valoir -1 (liste vide) — d'où le plancher à 0.
  const pageIndex = Math.min(requestedPage, Math.max(0, pageCount - 1));

  const half = Math.floor(maxPageButtonsCount / 2);
  const firstShownItemIndex = pageIndex * pageMaxItemCount;
  const maxShownItemIndexExcluded = firstShownItemIndex + pageMaxItemCount;

  return {
    firstShownItemIndex,
    getPageCountForContent,
    maxShownItemIndexExcluded,
    pageMaxItemCount,
    sliceVisibleContent: (content) =>
      (Array.isArray(content)
        ? content.slice(firstShownItemIndex, maxShownItemIndexExcluded)
        : content),
    PageControls: () => {
      if (!forcePaginationDisplay && pageCount <= 1) return null;

      // Fenêtre glissante de `maxPageButtonsCount` boutons autour de la page
      // courante, bornée aux deux extrémités.
      const buttons = [];
      let cursor = Math.min(Math.max(0, pageCount - maxPageButtonsCount), Math.max(0, pageIndex - half));
      for (let remaining = maxPageButtonsCount; remaining > 0 && cursor < pageCount; remaining--, cursor++) {
        const page = cursor;
        buttons.push(h(
          Item,
          { key: page, active: pageIndex === page, onClick: () => setRequestedPage(page), ...itemProps },
          page + 1,
        ));
      }

      // `createElement` plutôt que du JSX : ce fichier exporte un HOOK, pas un
      // composant — le garder en `.js` évite la règle Fast Refresh
      // (`react-refresh/only-export-components`), qui vide l'écran en dev sur
      // un `.jsx` exportant autre chose qu'un composant.
      return h(
        Container,
        containerProps,
        h(First, { disabled: pageIndex <= 0, onClick: () => setRequestedPage(0) }),
        h(Prev, { disabled: pageIndex <= 0, onClick: () => setRequestedPage(pageIndex - 1) }),
        Math.min(pageIndex - half, pageCount - maxPageButtonsCount) > 0
          ? h(Ellipsis, { key: 'lead', disabled: true })
          : null,
        buttons,
        pageCount > Math.max(half, pageIndex) + Math.ceil(maxPageButtonsCount / 2)
          ? h(Ellipsis, { key: 'trail', disabled: true })
          : null,
        h(Next, { disabled: pageIndex + 1 >= pageCount, onClick: () => setRequestedPage(pageIndex + 1) }),
        h(Last, { disabled: pageIndex + 1 >= pageCount, onClick: () => setRequestedPage(pageCount - 1) }),
      );
    },
  };
};

// Replis minimaux : le projet enregistre ses propres composants
// (`customPlugins.pagination`), ceux-ci ne servent qu'à ne jamais rendre null.
const DefaultContainer = ({ children, ...props }) =>
  h('nav', { 'aria-label': 'Pagination', ...props }, h('div', null, children));
const DefaultButton = ({ disabled, onClick, children }) =>
  h('button', { type: 'button', disabled, onClick }, children ?? '·');
const DefaultItem = ({ active, onClick, children }) =>
  h('button', { type: 'button', 'aria-current': active ? 'page' : undefined, onClick }, children);
const DefaultEllipsis = () => h('span', { 'aria-hidden': 'true' }, '…');
